import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  TableChart as ExcelIcon,
  TextFields as TextIcon,
  Code as JsonIcon,
  Html as HtmlIcon,
  TableChart as CsvIcon
} from '@mui/icons-material';
import ApiServiceFecth from 'service/ApiServiceFecth';

const ExportMenu = ({
  serverSideUrl,
  filters,
  sorts,
  columns,
  title = 'Datos',
  onExportStart,
  onExportComplete,
  onExportError,
  visibleColumns
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState('');

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (format) => {
    setExporting(true);
    setExportType(format);
    setAnchorEl(null);

    try {
      if (onExportStart) {
        onExportStart(format);
      }

      // Construir parámetros de exportación
      const params = {};

      // Agregar filtros
      if (filters && Object.keys(filters).length > 0) {
        params.filters = JSON.stringify(filters);
      }

      // Agregar ordenamiento
      if (sorts && Object.keys(sorts).length > 0) {
        // Filtrar solo los sorts que tienen valores válidos
        const validSorts = {};
        Object.keys(sorts).forEach(key => {
          if (key && sorts[key] && ['asc', 'desc'].includes(sorts[key].toLowerCase())) {
            validSorts[key] = sorts[key];
          }
        });

        if (Object.keys(validSorts).length > 0) {
          params.sorts = JSON.stringify(validSorts);
        }
      }

      // Agregar formato
      params.format = format;

      // Agregar columnas para exportación (solo las visibles)
      const exportColumns = columns
        .filter(col => col.name && col.export !== false && (!visibleColumns || visibleColumns.includes(col.name)))
        .map(col => ({
          name: col.name,
          label: col.label,
          type: col.filterType || 'text',
          booleanOptions: col.booleanOptions // Incluir opciones booleanas para exportación
        }));
      params.columns = JSON.stringify(exportColumns);

      // Agregar título
      params.title = title;

      // Usar el servicio de API para la descarga
      const apiService = ApiServiceFecth;
      const response = await apiService.download(serverSideUrl, params);

      if (!response.ok) {
        throw new Error(`Error en la exportación: ${response.status}`);
      }

      // Obtener el blob del archivo
      const blob = await response.blob();

      // Crear URL para descarga
      const url = window.URL.createObjectURL(blob);

      // Crear elemento de descarga
      const link = document.createElement('a');
      link.href = url;

      // Generar nombre de archivo
      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `${title}_${timestamp}.${getFileExtension(format)}`;
      link.download = fileName;

      // Descargar archivo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL
      window.URL.revokeObjectURL(url);

      if (onExportComplete) {
        onExportComplete(format);
      }

    } catch (error) {
      if (onExportError) {
        onExportError(error, format);
      }
    } finally {
      setExporting(false);
      setExportType('');
    }
  };

  const getFileExtension = (format) => {
    switch (format) {
      case 'excel':
        return 'xlsx';
      case 'csv':
        return 'csv';
      case 'txt':
        return 'txt';
      case 'json':
        return 'json';
      case 'html':
        return 'html';
      default:
        return 'txt';
    }
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'excel':
        return <ExcelIcon />;
      case 'csv':
        return <CsvIcon />;
      case 'txt':
        return <TextIcon />;
      case 'json':
        return <JsonIcon />;
      case 'html':
        return <HtmlIcon />;
      default:
        return <FileDownloadIcon />;
    }
  };

  const exportFormats = [
    { value: 'excel', label: 'Excel (.xlsx)', icon: getFormatIcon('excel') },
    { value: 'csv', label: 'CSV (Separado por comas)', icon: getFormatIcon('csv') },
    { value: 'txt', label: 'Texto (Columnas fijas)', icon: getFormatIcon('txt') },
    { value: 'json', label: 'JSON', icon: getFormatIcon('json') },
    { value: 'html', label: 'HTML', icon: getFormatIcon('html') }
  ];

  return (
    <>
      <Tooltip title="Exportar datos">
        <IconButton
          onClick={handleClick}
          disabled={exporting}
          sx={{
            "&:hover": (theme) => ({
              color: theme.palette.primary.main,
            }),
          }}
        >
          {exporting ? (
            <CircularProgress size={20} />
          ) : (
            <FileDownloadIcon />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {exportFormats.map((format) => (
          <MenuItem
            key={format.value}
            onClick={() => {
              handleExport(format.value);
            }}
            disabled={exporting && exportType === format.value}
          >
            <ListItemIcon>
              {exporting && exportType === format.value ? (
                <CircularProgress size={16} />
              ) : (
                format.icon
              )}
            </ListItemIcon>
            <ListItemText>
              {exporting && exportType === format.value
                ? `Exportando ${format.label}...`
                : format.label
              }
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

ExportMenu.propTypes = {
  serverSideUrl: PropTypes.string.isRequired,
  filters: PropTypes.object,
  sorts: PropTypes.object,
  columns: PropTypes.array.isRequired,
  title: PropTypes.string,
  onExportStart: PropTypes.func,
  onExportComplete: PropTypes.func,
  onExportError: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
};

export default ExportMenu;
